import { supabase } from '../lib/supabase';

// Types based on the database schema
export interface WeeklyLocking {
  id: string;
  user_id: string;
  locked_at: string;
  expires_at: string;
  status: 'pending' | 'active' | 'completed' | 'expired';
  total_time_minutes: number;
  completion_percentage: number;
  week_start_date: string;
  week_end_date: string;
  notes?: string;
  created_at: string;
  updated_at: string;
}

export interface LockedTalent {
  id: string;
  locking_id: string;
  talent_id: string;
  talent_name: string;
  target_minutes_per_week: number;
  tree_type: 'longevity' | 'healthspan' | 'wellness';
  completion_percentage: number;
  total_minutes_completed: number;
  selected_ranks?: number; // Number of ranks selected for this talent
  created_at: string;
}

export interface ProgressEntry {
  id: string;
  locking_id: string;
  talent_id: string;
  entry_date: string;
  completion_percentage: number;
  minutes_completed: number;
  method: 'app' | 'email' | 'sms' | 'api' | 'wearable';
  quality_rating?: number;
  notes?: string;
  created_at: string;
}

export interface NotificationPreferences {
  id: string;
  user_id: string;
  email_enabled: boolean;
  email_time: string;
  push_enabled: boolean;
  sms_enabled: boolean;
  phone_number?: string;
  timezone: string;
  reminder_frequency: 'daily' | 'twice_daily' | 'weekly' | 'none';
  motivational_emails: boolean;
  progress_summaries: boolean;
  created_at: string;
  updated_at: string;
}

export interface TalentSelection {
  talent_id: string;
  talent_name: string;
  target_minutes_per_week: number;
  tree_type: 'longevity' | 'healthspan' | 'wellness';
  selected_ranks?: number; // Number of ranks selected (for multi-rank talents)
  base_time_per_rank?: number; // Base time per rank for calculation
}

export interface WeeklyLockingWithTalents extends WeeklyLocking {
  locked_talents: LockedTalent[];
}

export interface ProgressSummary {
  talent_id: string;
  talent_name: string;
  target_minutes: number;
  completed_minutes: number;
  completion_percentage: number;
  daily_progress: {
    date: string;
    minutes: number;
    percentage: number;
  }[];
}

class WeeklyLockInService {
  
  // Lock-In Management
  
  /**
   * Get the current active locking for a user
   */
  async getCurrentLocking(userId: string): Promise<WeeklyLockingWithTalents | null> {
    try {
      const { data: locking, error } = await supabase
        .from('weekly_lockings')
        .select(`
          *,
          locked_talents (*)
        `)
        .eq('user_id', userId)
        .in('status', ['pending', 'active'])
        .order('created_at', { ascending: false })
        .limit(1)
        .single();

      if (error && error.code !== 'PGRST116') { // PGRST116 = no rows returned
        throw error;
      }

      return locking || null;
    } catch (error) {
      console.error('Error getting current locking:', error);
      throw error;
    }
  }

  /**
   * Check if user can create a new lock-in
   * Users can create lock-ins at any time if they don't have an active one
   */
  async isInLockInWindow(userId?: string): Promise<boolean> {
    // Allow test user to lock in at any time
    try {
      const { data: { user }, error } = await supabase.auth.getUser();
      if (!error && user?.email?.toLowerCase() === 'amarildo121@gmail.com') {
        console.log('Test user detected, allowing lock-in at any time');
        return true;
      }
    } catch (error) {
      console.warn('Could not check user email for testing override:', error);
    }

    // If userId is provided, check if they have an active lock-in
    if (userId) {
      try {
        const currentLocking = await this.getCurrentLocking(userId);
        // If no active lock-in, user can create one at any time
        if (!currentLocking) {
          return true;
        }
        // If they have an active lock-in, they can't create another one
        return false;
      } catch (error) {
        console.warn('Could not check current locking status:', error);
        // If we can't check, fall back to allowing creation
        return true;
      }
    }

    // If no userId provided, allow creation (for backward compatibility)
    return true;
  }

  /**
   * Calculate week start and end dates
   */
  getWeekDates(date: Date = new Date()): { startDate: Date; endDate: Date } {
    const startDate = new Date(date);
    const day = startDate.getDay();
    const diff = startDate.getDate() - day + (day === 0 ? -6 : 1); // Adjust when day is Sunday
    startDate.setDate(diff);
    startDate.setHours(0, 0, 0, 0);

    const endDate = new Date(startDate);
    endDate.setDate(startDate.getDate() + 6);
    endDate.setHours(23, 59, 59, 999);

    return { startDate, endDate };
  }

  /**
   * Create a new weekly lock-in
   */
  async createLocking(
    userId: string,
    talents: TalentSelection[],
    notes?: string
  ): Promise<WeeklyLockingWithTalents> {
    try {
      // Validate lock-in availability
      if (!(await this.isInLockInWindow(userId))) {
        throw new Error('You already have an active weekly lock-in. Complete or wait for your current lock-in to expire before creating a new one.');
      }

      // Validate talents
      if (talents.length < 3) {
        throw new Error('You must select at least 3 talents.');
      }

      const totalMinutes = talents.reduce((sum, talent) => sum + talent.target_minutes_per_week, 0);
      if (totalMinutes > 1500) { // 25 hours max
        throw new Error('Total time commitment cannot exceed 1500 minutes (25 hours) per week.');
      }

      // Get week dates
      const { startDate, endDate } = this.getWeekDates();
      const expiresAt = new Date(endDate);

      // Create the weekly locking
      const { data: locking, error: lockingError } = await supabase
        .from('weekly_lockings')
        .insert({
          user_id: userId,
          expires_at: expiresAt.toISOString(),
          total_time_minutes: totalMinutes,
          week_start_date: startDate.toISOString().split('T')[0],
          week_end_date: endDate.toISOString().split('T')[0],
          notes,
          status: 'active'
        })
        .select()
        .single();

      if (lockingError) throw lockingError;

      // Create locked talents
      const lockedTalentsData = talents.map(talent => ({
        locking_id: locking.id,
        talent_id: talent.talent_id,
        talent_name: talent.talent_name,
        target_minutes_per_week: talent.target_minutes_per_week,
        tree_type: talent.tree_type
      }));

      const { data: lockedTalents, error: talentsError } = await supabase
        .from('locked_talents')
        .insert(lockedTalentsData)
        .select();

      if (talentsError) throw talentsError;

      const result = {
        ...locking,
        locked_talents: lockedTalents
      };
      
      console.log('✅ [WeeklyLockInService] Lock-in creation completed successfully:', result);
      return result;
    } catch (error) {
      console.error('❌ [WeeklyLockInService] Error creating locking:', error);
      throw error;
    }
  }

  /**
   * Update an existing lock-in (only if status is 'pending')
   */
  async updateLocking(
    lockingId: string,
    talents: TalentSelection[],
    notes?: string
  ): Promise<WeeklyLockingWithTalents> {
    try {
      // Get current locking to check status
      const { data: currentLocking } = await supabase
        .from('weekly_lockings')
        .select('status')
        .eq('id', lockingId)
        .single();

      if (currentLocking?.status !== 'pending') {
        throw new Error('Can only update lock-ins in pending status.');
      }

      // Calculate new total minutes
      const totalMinutes = talents.reduce((sum, talent) => sum + talent.target_minutes_per_week, 0);

      // Update the locking
      const { data: locking, error: lockingError } = await supabase
        .from('weekly_lockings')
        .update({
          total_time_minutes: totalMinutes,
          notes,
          updated_at: new Date().toISOString()
        })
        .eq('id', lockingId)
        .select()
        .single();

      if (lockingError) throw lockingError;

      // Delete existing locked talents
      await supabase
        .from('locked_talents')
        .delete()
        .eq('locking_id', lockingId);

      // Create new locked talents
      const lockedTalentsData = talents.map(talent => ({
        locking_id: lockingId,
        talent_id: talent.talent_id,
        talent_name: talent.talent_name,
        target_minutes_per_week: talent.target_minutes_per_week,
        tree_type: talent.tree_type
      }));

      const { data: lockedTalents, error: talentsError } = await supabase
        .from('locked_talents')
        .insert(lockedTalentsData)
        .select();

      if (talentsError) throw talentsError;

      return {
        ...locking,
        locked_talents: lockedTalents
      };
    } catch (error) {
      console.error('Error updating locking:', error);
      throw error;
    }
  }

  /**
   * Cancel a lock-in (only if status is 'pending')
   */
  async cancelLocking(lockingId: string): Promise<void> {
    try {
      const { error } = await supabase
        .from('weekly_lockings')
        .delete()
        .eq('id', lockingId);

      if (error) throw error;
    } catch (error) {
      console.error('Error canceling locking:', error);
      throw error;
    }
  }

  /**
   * Reset current lock-in for testing (delete current weekly locking and all related data)
   */
  async resetCurrentLockIn(): Promise<void> {
    try {
      const { data: currentUser, error: userError } = await supabase.auth.getUser();
      if (userError) throw userError;
      
      if (!currentUser.user?.email || currentUser.user.email !== 'amarildo121@gmail.com') {
        throw new Error('Reset functionality is only available for test users');
      }

      // Get current active locking
      const { data: lockings, error: lockingError } = await supabase
        .from('weekly_lockings')
        .select('id')
        .eq('user_id', currentUser.user.id)
        .eq('status', 'active')
        .single();

      if (lockingError && lockingError.code !== 'PGRST116') throw lockingError;
      
      if (lockings) {
        // Delete progress entries first (due to foreign key constraints)
        await supabase
          .from('progress_entries')
          .delete()
          .eq('locking_id', lockings.id);

        // Delete locked talents
        await supabase
          .from('locked_talents')
          .delete()
          .eq('locking_id', lockings.id);

        // Delete the weekly locking
        await supabase
          .from('weekly_lockings')
          .delete()
          .eq('id', lockings.id);
      }
    } catch (error) {
      console.error('Error resetting lock-in:', error);
      throw error;
    }
  }

  // Progress Tracking

  /**
   * Submit daily progress for a talent
   */
  async submitProgress(
    lockingId: string,
    talentId: string,
    minutesCompleted: number,
    completionPercentage: number,
    method: ProgressEntry['method'] = 'app',
    qualityRating?: number,
    notes?: string,
    entryDate?: Date
  ): Promise<ProgressEntry> {
    try {
      const date = entryDate || new Date();
      const dateString = date.toISOString().split('T')[0];

      const progressData = {
        locking_id: lockingId,
        talent_id: talentId,
        entry_date: dateString,
        completion_percentage: completionPercentage,
        minutes_completed: minutesCompleted,
        method,
        quality_rating: qualityRating,
        notes
      };

      // Use upsert to handle daily updates
      const { data, error } = await supabase
        .from('progress_entries')
        .upsert(progressData, {
          onConflict: 'locking_id,talent_id,entry_date'
        })
        .select()
        .single();

      if (error) throw error;

      return data;
    } catch (error) {
      console.error('Error submitting progress:', error);
      throw error;
    }
  }

  /**
   * Get progress for a specific locking
   */
  async getProgress(lockingId: string): Promise<ProgressEntry[]> {
    try {
      const { data, error } = await supabase
        .from('progress_entries')
        .select('*')
        .eq('locking_id', lockingId)
        .order('entry_date', { ascending: true });

      if (error) throw error;

      return data || [];
    } catch (error) {
      console.error('Error getting progress:', error);
      throw error;
    }
  }

  /**
   * Get progress summary for a locking
   */
  async getProgressSummary(lockingId: string): Promise<ProgressSummary[]> {
    try {
      // Get locked talents
      const { data: lockedTalents, error: talentsError } = await supabase
        .from('locked_talents')
        .select('*')
        .eq('locking_id', lockingId);

      if (talentsError) throw talentsError;

      // Get progress entries
      const { data: progressEntries, error: progressError } = await supabase
        .from('progress_entries')
        .select('*')
        .eq('locking_id', lockingId)
        .order('entry_date', { ascending: true });

      if (progressError) throw progressError;

      // Build summary
      const summary: ProgressSummary[] = lockedTalents.map(talent => {
        const talentProgress = progressEntries.filter(p => p.talent_id === talent.talent_id);
        const totalCompleted = talentProgress.reduce((sum, p) => sum + p.minutes_completed, 0);
        const completionPercentage = talent.target_minutes_per_week > 0 
          ? Math.min((totalCompleted / talent.target_minutes_per_week) * 100, 100)
          : 0;

        return {
          talent_id: talent.talent_id,
          talent_name: talent.talent_name,
          target_minutes: talent.target_minutes_per_week,
          completed_minutes: totalCompleted,
          completion_percentage: completionPercentage,
          daily_progress: talentProgress.map(p => ({
            date: p.entry_date,
            minutes: p.minutes_completed,
            percentage: p.completion_percentage
          }))
        };
      });

      return summary;
    } catch (error) {
      console.error('Error getting progress summary:', error);
      throw error;
    }
  }

  /**
   * Reset progress for a locking (delete all progress entries)
   */
  async resetProgress(lockingId: string): Promise<void> {
    try {
      const { error } = await supabase
        .from('progress_entries')
        .delete()
        .eq('locking_id', lockingId);

      if (error) throw error;
    } catch (error) {
      console.error('Error resetting progress:', error);
      throw error;
    }
  }

  // Notification Preferences

  /**
   * Get user's notification preferences
   */
  async getNotificationPreferences(userId: string): Promise<NotificationPreferences | null> {
    try {
      const { data, error } = await supabase
        .from('notification_preferences')
        .select('*')
        .eq('user_id', userId)
        .single();

      if (error && error.code !== 'PGRST116') {
        throw error;
      }

      return data || null;
    } catch (error) {
      console.error('Error getting notification preferences:', error);
      throw error;
    }
  }

  /**
   * Update notification preferences
   */
  async updateNotificationPreferences(
    userId: string,
    preferences: Partial<Omit<NotificationPreferences, 'id' | 'user_id' | 'created_at' | 'updated_at'>>
  ): Promise<NotificationPreferences> {
    try {
      const { data, error } = await supabase
        .from('notification_preferences')
        .upsert({
          user_id: userId,
          ...preferences,
          updated_at: new Date().toISOString()
        }, {
          onConflict: 'user_id'
        })
        .select()
        .single();

      if (error) throw error;

      return data;
    } catch (error) {
      console.error('Error updating notification preferences:', error);
      throw error;
    }
  }

  // Reporting and Analytics

  /**
   * Get user's locking history
   */
  async getLockingHistory(userId: string, limit: number = 10): Promise<WeeklyLockingWithTalents[]> {
    try {
      const { data, error } = await supabase
        .from('weekly_lockings')
        .select(`
          *,
          locked_talents (*)
        `)
        .eq('user_id', userId)
        .order('created_at', { ascending: false })
        .limit(limit);

      if (error) throw error;

      return data || [];
    } catch (error) {
      console.error('Error getting locking history:', error);
      throw error;
    }
  }

  /**
   * Calculate user streaks
   */
  async calculateStreaks(userId: string): Promise<{
    currentStreak: number;
    longestStreak: number;
    totalCompletedWeeks: number;
  }> {
    try {
      const { data: lockings, error } = await supabase
        .from('weekly_lockings')
        .select('completion_percentage, week_start_date')
        .eq('user_id', userId)
        .eq('status', 'completed')
        .order('week_start_date', { ascending: false });

      if (error) throw error;

      if (!lockings || lockings.length === 0) {
        return { currentStreak: 0, longestStreak: 0, totalCompletedWeeks: 0 };
      }

      // Count completed weeks (80%+ completion as per PRD)
      const completedWeeks = lockings.filter(l => l.completion_percentage >= 80);
      const totalCompletedWeeks = completedWeeks.length;

      // Calculate current streak
      let currentStreak = 0;
      for (const locking of completedWeeks) {
        if (locking.completion_percentage >= 80) {
          currentStreak++;
        } else {
          break;
        }
      }

      // Calculate longest streak
      let longestStreak = 0;
      let tempStreak = 0;
      for (const locking of lockings.reverse()) {
        if (locking.completion_percentage >= 80) {
          tempStreak++;
          longestStreak = Math.max(longestStreak, tempStreak);
        } else {
          tempStreak = 0;
        }
      }

      return { currentStreak, longestStreak, totalCompletedWeeks };
    } catch (error) {
      console.error('Error calculating streaks:', error);
      throw error;
    }
  }

  /**
   * Check and update expired lockings
   */
  async updateExpiredLockings(): Promise<void> {
    try {
      const now = new Date().toISOString();

      const { error } = await supabase
        .from('weekly_lockings')
        .update({ 
          status: 'expired',
          updated_at: now
        })
        .lt('expires_at', now)
        .in('status', ['active', 'pending']);

      if (error) throw error;
    } catch (error) {
      console.error('Error updating expired lockings:', error);
      throw error;
    }
  }
}

export const weeklyLockInService = new WeeklyLockInService(); 